import { useQuery, useMutation } from '@apollo/client/react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Grid, Paper, Typography } from '@mui/material';
import { EssealDataTable } from 'esseal-data-table';
import { filter, find } from 'lodash';
import { useContext, useState } from 'react';

import subContractorRegisterColumns from './subContractorRegisterColumns';
import SubContractorRegisterToolbar from './Toolbar';

import EditScheduleModal from '@/components/Scheduling/EditScheduleModal';
import NewScheduleModal from '@/components/Scheduling/NewScheduleModal';
import CurrentUserContext from '@/Contexts/CurrentUser';
import PermissionContext from '@/Contexts/Permissions';
import { ReportContext } from '@/Contexts/ReportContext';
import {
  GetSchedulesDocument,
  CompanyResourceResourceTypeEnum,
  UpdateScheduleDocument,
  PermissionActionEnum,
  PermissionEntityEnum,
  ResourceScheduleStatusEnum,
  type Schedule,
} from '@/graphql/generated/graphql';
import { StyledRegisterHead } from '@/theme/styled-components';
import { displayNotification } from '@/utils/notifications';

const SubContractorRegister: React.FC = () => {
  const { dateTimePreference } = useContext(CurrentUserContext);
  const { can } = useContext(PermissionContext);
  const { selectedReport, selectedProject, projectShifts, projectHolidays } = useContext(ReportContext);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedSubContractorId, setSelectedSubContractorId] = useState<string | null>(null);

  const { data, loading } = useQuery(GetSchedulesDocument, {
    skip: !selectedReport,
    variables: {
      projectId: selectedReport?.projectId as string,
      date: selectedReport?.reportDate as string,
      resourceType: CompanyResourceResourceTypeEnum.SubContractor,
    },
  });

  const subContractorSchedules = filter(
    data?.getSchedules || [],
    (schedule) => schedule.companyResource?.resourceType === CompanyResourceResourceTypeEnum.SubContractor,
  );

  const [unschedule] = useMutation(UpdateScheduleDocument);

  const handleAddFunction = () => {
    if (!can(PermissionActionEnum.Create, PermissionEntityEnum.Scheduling, { projectId: selectedProject?.id })) return;

    setOpenModal(true);
  };

  const handleEditFunction = (id: string) => {
    if (!can(PermissionActionEnum.Edit, PermissionEntityEnum.Scheduling, { projectId: selectedProject?.id })) return;

    setSelectedSubContractorId(id);
  };

  const handleDeleteFunction = (id: string) => {
    if (!can(PermissionActionEnum.Manage, PermissionEntityEnum.Scheduling, { projectId: selectedProject?.id })) return;
    if (!selectedReport) return;

    unschedule({
      variables: {
        schedule: {
          id,
          status: ResourceScheduleStatusEnum.Dropped,
        },
      },
      refetchQueries: [
        {
          query: GetSchedulesDocument,
          variables: {
            projectId: selectedReport.projectId,
            date: selectedReport.reportDate,
            resourceType: CompanyResourceResourceTypeEnum.SubContractor,
          },
        },
      ],
      onCompleted: () => displayNotification({ type: 'success', text: 'Subcontractor schedule removed successfully' }),
      onError: (error) => displayNotification({ type: 'error', text: error.message }),
    });
  };

  if (!selectedReport) return <></>;

  const selectedSchedule = find(subContractorSchedules, (schedule) => schedule.id === selectedSubContractorId);

  return (
    <Grid component={Paper} size={12}>
      <StyledRegisterHead>
        <Typography variant="caption" fontWeight={700}>
          Subcontractor Register
        </Typography>
      </StyledRegisterHead>
      <EssealDataTable
        height={320}
        rows={(data?.getSchedules as Schedule[]) || []}
        columns={subContractorRegisterColumns(dateTimePreference.time as string)}
        loading={loading}
        maxVisibleActions={2}
        toolbar={<SubContractorRegisterToolbar onAddRecord={handleAddFunction} />}
        rowActions={() => [
          {
            label: 'Edit',
            icon: <EditIcon fontSize="small" />,
            tooltip: 'Edit Schedule',
            onClick: (row) => handleEditFunction(row.id),
          },
          {
            label: 'Delete',
            icon: <DeleteIcon fontSize="small" />,
            tooltip: 'Delete Schedule',
            onClick: (row) => handleDeleteFunction(row.id),
          },
        ]}
      />

      {openModal && (
        <NewScheduleModal
          addresses={selectedProject?.addresses || []}
          resourceType={CompanyResourceResourceTypeEnum.SubContractor}
          reportDate={selectedReport.reportDate}
          projectId={selectedProject?.id as string}
          reportId={selectedReport?.id as string}
          projectShifts={projectShifts}
          projectHolidays={projectHolidays}
          handleClose={() => setOpenModal(false)}
        />
      )}

      {selectedSubContractorId && selectedSchedule && (
        <EditScheduleModal handleClose={() => setSelectedSubContractorId(null)} selectedSchedule={selectedSchedule} />
      )}
    </Grid>
  );
};

export default SubContractorRegister;
